import { LoadingButton } from '@mui/lab';
import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, FormControl,
    TextField, Typography,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { SeasonShowContext } from '../../Providers/SeasonShowProvider';
import { SnackbarContext } from '../../Providers/SnackbarProvider';
import { useNavigate } from 'react-router-dom';
import { graphql } from 'gql';
import { namedOperations } from 'gql/graphql';

const CreateSeasonForShow = graphql(`
    mutation CreateSeason_AddSeasonDialog($input: [SeasonCreateInput!]!) {
        createSeasons(input: $input) {
            seasons {
                id
                title
                totalVoices
            }
        }
    }`);

type Props = {
    open: boolean;
    onClose: () => void;
}

export default function AddSeasonDialog({ open, onClose }: Props) {
    const navigate = useNavigate();

    const { selectedShow, setSelectedSeason } = useContext(SeasonShowContext);
    const { success, error } = useContext(SnackbarContext);

    const [title, setTitle] = useState<string>();

    useEffect(() => {
        if (open) {
            setTitle(undefined);
        }
    }, [open])

    const [createSeasonForShow, { loading }] = useMutation(CreateSeasonForShow);

    const handleSave = async () => {
        try {
            if (!title) {
                error('Title is required');
                return;
            }

            const result = await createSeasonForShow({
                variables: {
                    input: [{
                        title,
                        show: {
                            connect: {
                                where: {
                                    node: {
                                        id: selectedShow!.id,
                                    },
                                },
                            },
                        },
                    }],
                },
                refetchQueries: [namedOperations.Query.GetSeasonFromShow_SeasonShowProvider],
            });
            const season = result.data?.createSeasons?.seasons?.[0];

            if (!season) {
                error('Failed to create show');
                return;
            }

            setSelectedSeason(season);
            navigate('/');

            success('Show created');
            handleClose();
        } catch (e) {
            // @ts-ignore
            error('Failed to create season. ' + e.message);
        }
    }

    const handleClose = () => {
        setTitle(undefined);
        onClose();
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            handleSave();
        }
    }

    if (!selectedShow) return null;

    return (
        <Dialog open={open}>
            <DialogTitle>Add Season</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1, mb: 1 }}>
                        <Typography>Show: {selectedShow!.title}</Typography>
                    </Box>
                </Box>
                <FormControl component="fieldset">
                    <TextField
                        sx={{ mt: 1, mb: 1 }}
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant='text'>Cancel</Button>
                <LoadingButton
                    onClick={handleSave}
                    disabled={!title}
                    variant='contained'
                    loading={loading}
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}